import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, addDoc, updateDoc, collection, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { useAuth } from '../context/AuthContext';
import TopNav from '../components/TopNav';
import GradientButton from '../components/GradientButton';
import { colors } from '../theme';

interface JobData {
  customerId: string;
  technicianId?: string;
  techId?: string;
  techName?: string;
  status: string;
  category: string;
  pricing?: {
    hourlyRate?: number;
    serviceFee?: number;
    emergencyFee?: number;
    minutesWorked?: number;
  };
  payment?: {
    stripePaymentIntentId?: string;
  };
  timer?: {
    startedAt?: any;
    endedAt?: any;
  };
}

const POSITIVE_TAGS = ['Fast', 'Friendly', 'Knowledgeable', 'Clean & Tidy', 'Great Value', 'Would Book Again'];
const NEGATIVE_TAGS = ['Late', "Didn't Fully Fix", 'Hard to Communicate', 'Left a Mess'];

type TipOption = 'none' | '$5' | '$10' | '$15' | 'custom';

export default function ReviewPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [job, setJob] = useState<JobData | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [tipOption, setTipOption] = useState<TipOption>('none');
  const [customTip, setCustomTip] = useState('');

  useEffect(() => {
    if (!jobId) return;
    getDoc(doc(db, 'jobs', jobId)).then((snap) => {
      if (snap.exists()) setJob(snap.data() as JobData);
      setLoading(false);
    });
  }, [jobId]);

  const getTipAmount = (): number => {
    switch (tipOption) {
      case '$5': return 5;
      case '$10': return 10;
      case '$15': return 15;
      case 'custom': return parseFloat(customTip) || 0;
      default: return 0;
    }
  };

  const minutesWorked = job?.pricing?.minutesWorked || 0;
  const hourlyRate = job?.pricing?.hourlyRate || 65;
  const serviceFee = job?.pricing?.serviceFee || 0;
  const emergencyFee = job?.pricing?.emergencyFee || 0;
  const laborCost = hourlyRate * (minutesWorked / 60);
  const tipAmount = getTipAmount();
  const totalAmount = laborCost + serviceFee + emergencyFee + tipAmount;

  const canSubmit = rating > 0 && (rating >= 5 || comment.trim().length > 0);

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!canSubmit || !user || !jobId || !job) return;
    setError('');
    setSubmitting(true);

    try {
      const techId = job.technicianId || job.techId || '';

      // Process tip if applicable
      const tip = getTipAmount();
      if (tip > 0 && job.payment?.stripePaymentIntentId) {
        try {
          const addTip = httpsCallable(functions, 'addTip');
          await addTip({ jobId, tipAmountCents: Math.round(tip * 100) });
        } catch (e) {
          console.warn('Tip processing failed, continuing with review:', e);
        }
      }

      // Write review
      const reviewRef = await addDoc(collection(db, 'reviews'), {
        jobId,
        technicianId: techId,
        customerId: user.uid,
        rating,
        comment: comment || '',
        tags: Array.from(selectedTags),
        tipAmount: tip,
        createdAt: serverTimestamp(),
      });

      // Update job
      await updateDoc(doc(db, 'jobs', jobId), {
        rating,
        tipAmount: tip,
        reviewSubmitted: true,
        reviewId: reviewRef.id,
      });

      navigate('/home', { replace: true });
    } catch (e: any) {
      setError('Failed to submit review. Please try again.');
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const ratingLabel = ['', '😞 Poor', '😐 Fair', '🙂 Good', '😊 Great', '🤩 Excellent!'][rating] || 'Tap a star to rate';
  const displayRating = hoverRating || rating;

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: colors.background,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            border: `3px solid ${colors.primary}`,
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 0.7s linear infinite',
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const techName = job?.techName || 'Your Technician';
  const techFirst = techName.split(' ')[0];

  return (
    <div
      style={{
        minHeight: '100vh',
        background: colors.background,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        paddingBottom: 40,
      }}
    >
      <TopNav title="Complete Job" />

      <div style={{ padding: '20px 16px 0' }}>
        {/* Tech avatar */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div
            style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'rgba(102,51,153,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 36,
              fontWeight: 700,
              color: colors.primary,
              margin: '0 auto 12px',
            }}
          >
            {techName.charAt(0)}
          </div>
          <h2 style={{ color: colors.textPrimary, fontSize: 22, fontWeight: 700, margin: 0 }}>{techName}</h2>
          <p style={{ color: colors.textSecondary, margin: '4px 0 0', fontSize: 14 }}>
            How did it go?
          </p>
        </div>

        {/* Star rating */}
        <div
          style={{
            background: colors.surface,
            borderRadius: 16,
            padding: '20px',
            marginBottom: 16,
            textAlign: 'center',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 12 }}>
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 4,
                  fontSize: 42,
                  color: star <= displayRating ? colors.gold : 'rgba(26,10,46,0.12)',
                  transform: star <= displayRating ? 'scale(1.08)' : 'scale(1)',
                  transition: 'transform 0.12s, color 0.12s',
                  lineHeight: 1,
                }}
              >
                ★
              </button>
            ))}
          </div>
          <p style={{ color: colors.textSecondary, margin: 0, fontSize: 15 }}>{ratingLabel}</p>
        </div>

        {/* Tags */}
        {rating > 0 && (
          <div
            style={{
              background: colors.surface,
              borderRadius: 16,
              padding: '16px',
              marginBottom: 16,
            }}
          >
            <p style={{ color: colors.textPrimary, fontWeight: 700, fontSize: 15, margin: '0 0 12px' }}>
              {rating >= 4 ? 'What stood out?' : 'What could be better?'}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {(rating >= 4 ? POSITIVE_TAGS : NEGATIVE_TAGS).map((tag) => (
                <button
                  key={tag}
                  onClick={() => toggleTag(tag)}
                  style={{
                    background: selectedTags.has(tag) ? colors.primary : 'rgba(102,51,153,0.1)',
                    border: `1.5px solid ${selectedTags.has(tag) ? colors.primary : 'transparent'}`,
                    borderRadius: 20,
                    padding: '7px 14px',
                    color: selectedTags.has(tag) ? '#fff' : colors.primary,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
                    transition: 'background 0.15s',
                  }}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Comment */}
        {rating > 0 && (
          <div
            style={{
              background: colors.surface,
              borderRadius: 16,
              padding: '16px',
              marginBottom: 16,
            }}
          >
            <p style={{ color: colors.textPrimary, fontWeight: 700, fontSize: 15, margin: '0 0 10px' }}>
              {rating < 5 ? (
                <span>
                  What could have gone better?{' '}
                  <span style={{ color: colors.error }}>*</span>
                </span>
              ) : (
                'Add a comment (optional)'
              )}
            </p>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={rating < 5 ? 'Please describe what could have been better…' : 'Tell us more about your experience…'}
              rows={4}
              style={{
                width: '100%',
                padding: '12px 14px',
                background: 'rgba(255,255,255,0.05)',
                border: `1.5px solid ${rating < 5 && comment.trim() === '' ? 'rgba(255,69,58,0.4)' : 'rgba(26,10,46,0.1)'}`,
                borderRadius: 12,
                color: colors.textPrimary,
                fontSize: 15,
                fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
                outline: 'none',
                resize: 'none',
                boxSizing: 'border-box',
              }}
            />
            {rating < 5 && comment.trim() === '' && (
              <p style={{ color: colors.error, fontSize: 12, margin: '6px 0 0' }}>
                Required to submit
              </p>
            )}
          </div>
        )}

        {/* Tip selector */}
        <div
          style={{
            background: colors.surface,
            borderRadius: 16,
            padding: '16px',
            marginBottom: 16,
          }}
        >
          <p style={{ color: colors.textPrimary, fontWeight: 700, fontSize: 15, margin: '0 0 12px' }}>
            Add a tip for {techFirst}? 💰
          </p>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            {(['$5', '$10', '$15', 'custom'] as TipOption[]).map((opt) => (
              <button
                key={opt}
                onClick={() => {
                  setTipOption(tipOption === opt ? 'none' : opt);
                  if (opt !== 'custom') setCustomTip('');
                }}
                style={{
                  flex: 1,
                  padding: '10px 6px',
                  background: tipOption === opt ? colors.primary : 'rgba(102,51,153,0.1)',
                  border: `1.5px solid ${tipOption === opt ? colors.primary : 'transparent'}`,
                  borderRadius: 12,
                  color: tipOption === opt ? '#fff' : colors.primary,
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
                }}
              >
                {opt}
              </button>
            ))}
          </div>
          {tipOption === 'custom' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ color: colors.primary, fontSize: 20, fontWeight: 700 }}>$</span>
              <input
                type="number"
                value={customTip}
                onChange={(e) => setCustomTip(e.target.value)}
                placeholder="Enter amount"
                min="1"
                step="1"
                style={{
                  flex: 1,
                  padding: '12px 14px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1.5px solid rgba(26,10,46,0.1)',
                  borderRadius: 12,
                  color: colors.textPrimary,
                  fontSize: 16,
                  outline: 'none',
                  fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
                }}
              />
            </div>
          )}
          {tipAmount > 0 && (
            <p style={{ color: colors.success, fontSize: 12, margin: '10px 0 0', textAlign: 'center' }}>
              ❤️ 100% of your tip goes directly to {techFirst}
            </p>
          )}
        </div>

        {/* Price breakdown + pay */}
        <div
          style={{
            background: colors.surface,
            borderRadius: 16,
            padding: '16px 18px',
            marginBottom: 20,
          }}
        >
          <p style={{ color: colors.textPrimary, fontWeight: 700, fontSize: 15, margin: '0 0 14px' }}>
            Payment Summary
          </p>
          {([
            minutesWorked > 0 ? [`Labor (${minutesWorked} min)`, `$${laborCost.toFixed(2)}`] : null,
            serviceFee > 0 ? ['Platform Fee', `$${serviceFee.toFixed(2)}`] : null,
            emergencyFee > 0 ? ['Emergency Fee', `$${emergencyFee.toFixed(2)}`] : null,
            tipAmount > 0 ? ['Tip', `$${tipAmount.toFixed(2)}`] : null,
          ] as [string, string][])
            .filter((item): item is [string, string] => item !== null)
            .map(([label, value]) => (
              <div
                key={label as string}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 8,
                }}
              >
                <span style={{ color: colors.textSecondary, fontSize: 14 }}>{label}</span>
                <span style={{ color: colors.textPrimary, fontSize: 14 }}>{value}</span>
              </div>
            ))}
          <div style={{ height: 1, background: colors.divider, margin: '10px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: colors.textPrimary, fontWeight: 700, fontSize: 16 }}>Total</span>
            <span style={{ color: colors.primary, fontWeight: 700, fontSize: 16 }}>
              ${totalAmount.toFixed(2)}
            </span>
          </div>
        </div>

        {error && (
          <p style={{ color: colors.error, fontSize: 14, textAlign: 'center', marginBottom: 12 }}>
            {error}
          </p>
        )}

        <GradientButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          loading={submitting}
          fullWidth
          style={{ fontSize: 17, padding: '15px 24px' }}
        >
          Pay ${totalAmount.toFixed(2)} & Submit Rating
        </GradientButton>

        {!canSubmit && rating > 0 && rating < 5 && (
          <p style={{ color: colors.textSecondary, fontSize: 12, textAlign: 'center', marginTop: 8 }}>
            Please describe what could have gone better before submitting.
          </p>
        )}

        <button
          style={{
            display: 'block',
            width: '100%',
            marginTop: 12,
            padding: '12px',
            background: 'none',
            border: 'none',
            color: 'rgba(26,10,46,0.15)',
            fontSize: 13,
            cursor: 'pointer',
            textDecoration: 'underline',
            fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
          }}
          onClick={() => navigate('/home')}
        >
          Something wasn't right? Dispute this charge
        </button>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
